/**
 * The top-bar entry point to Ask Dink (DNKADM-0434 U2): the "Ask Dink" button, the
 * `?` keyboard shortcut, and the panel itself.
 *
 * - The screen list is built per render from the router tree (supplied by
 *   `AskDinkRoutesContext`) and this session's role + Front Desk state, so it always
 *   matches what the router would let this session open.
 * - `?` opens the panel unless focus is in an input, textarea, select or
 *   contenteditable region, or a modifier is held.
 * - The assistant shows only once `GET /v2/admin/assistant/status` answers
 *   `enabled: true` (DNKADM-0455). While that is loading, or if it fails, nothing
 *   renders (fail closed). A `404 ASSISTANT_DISABLED` from an ask stays as a backstop:
 *   it removes the button, shortcut and panel for the rest of the session. Both facts
 *   live only in this component's state (the shell stays mounted for the session),
 *   never in storage.
 * - Turning Ask Dink on or off in Settings (DNKADM-0461) signals through
 *   `assistantStatusRefresh`; the launcher re-reads the status and clears the backstop, so
 *   the button appears or disappears without a reload.
 * - DNKADM-0460: the panel is told the current screen (`askDinkScreen`), the access
 *   its answers match (`Staff` for the staff floor, `Owner` for OWNER/ADMIN/SUPER_ADMIN,
 *   read from the token's role, the same thing dink-api filters on), the operator's
 *   first name, the selected location's name, and whether it is a phone (under 768 px:
 *   a full-screen sheet). The panel is keyed on the signed-in operator, so a sign-out
 *   (operator gone) or a different operator remounts it and drops this session's
 *   questions.
 */
import { Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAssistantStatus } from '@/api/assistant';
import { useCurrentOperator } from '@/auth/authHooks';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { useResource } from '@/hooks/useResource';
import { IconAction } from '@/components/IconAction';
import { ServiceLocationContext } from '@/location/locationContextValue';
import { Icon } from '@/theme';
import { AskDinkPanel } from './AskDinkPanel';
import { askDinkScreen } from './askDinkScreen';
import { AskDinkRoutesContext } from './askDinkRoutesContext';
import { onAssistantStatusRefresh } from './assistantStatusRefresh';
import { buildAskDinkRoutes } from './routeCatalogue';

/** Below this width the panel is a full-screen sheet (DNKADM-0460 item 8). */
const PHONE_SHEET_BELOW = 768;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select')) return true;
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

export function AskDinkLauncher() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPhone = useMediaQuery(theme.breakpoints.down(PHONE_SHEET_BELOW));
  const { pathname } = useLocation();
  // Read directly: the panel renders without a LocationProvider in some trees, and a
  // missing location just leaves "Help for …" out.
  const locationName = useContext(ServiceLocationContext)?.selected?.name ?? null;
  const operator = useCurrentOperator();
  const { frontDeskView } = useFrontDeskView();
  const routeTree = useContext(AskDinkRoutesContext);
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  // One status read on mount; loading and error both leave the assistant hidden.
  const status = useResource(getAssistantStatus, []);

  const routes = buildAskDinkRoutes(routeTree, {
    isAdmin: operator?.isAdmin ?? false,
    isStaffTier: operator?.isStaffTier ?? false,
    frontDeskView,
  });

  const { refetch: refetchStatus } = status;
  // A subscription to the Settings screen's "status changed" signal — an external source,
  // so an effect is the right tool.
  useEffect(
    () =>
      onAssistantStatusRefresh(() => {
        setDisabled(false);
        refetchStatus();
      }),
    [refetchStatus],
  );

  const hidden = disabled || !(status.status === 'ready' && status.data === true);

  // A document-level key listener is a subscription to the DOM — the one job an
  // effect is for.
  useEffect(() => {
    if (hidden) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      event.preventDefault();
      setOpen(true);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hidden]);

  if (hidden) return null;

  return (
    <>
      {isMobile ? (
        <IconAction
          label="Ask Dink"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          sx={{ width: 44, height: 44 }}
        >
          <Icon name="support_agent" sx={{ fontSize: 22 }} />
        </IconAction>
      ) : (
        <Button
          size="small"
          color="inherit"
          aria-expanded={open}
          aria-keyshortcuts="?"
          startIcon={<Icon name="support_agent" sx={{ fontSize: 20 }} />}
          onClick={() => setOpen(true)}
        >
          Ask Dink
        </Button>
      )}
      <AskDinkPanel
        key={operator?.email ?? 'signed-out'}
        open={open}
        onClose={() => setOpen(false)}
        routes={routes}
        screen={askDinkScreen(pathname)}
        role={operator?.isStaffTier === true ? 'Staff' : 'Owner'}
        firstName={operator?.name?.trim().split(/\s+/)[0] || null}
        locationName={locationName}
        isPhone={isPhone}
        onDisabled={() => {
          setOpen(false);
          setDisabled(true);
        }}
      />
    </>
  );
}
